import { Component, ChangeDetectionStrategy, Input, ChangeDetectorRef, Inject, InjectionToken} from '@angular/core';
import { AvailableColumns, SelectedColumns, ColumnSettingInfo } from '../column-settings';
import { uniqBy } from 'lodash';
import { MdlDialogReference } from '@angular-mdl/core';
import { CdkDragDrop, CdkDragStart, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
export const COLUMN_SETTING_TOKEN = new InjectionToken('ColumnSettingItem');

@Component({
    selector: 'app-column-settings',
    template: require('./column-settings.component.html'),
    styles: [require('./column-settings.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ColumnSettingsComponent {

    @Input()
    public availableColumns: AvailableColumns[];

    @Input()
    public selectedColumns: SelectedColumns[];
    public settingsInfo: ColumnSettingInfo;
    public header = 'Column View Settings';
    public subheaderText = 'View';
    public selectedElement: HTMLElement = null;
    public selectedelementId = '';
    public multiSelectItems: SelectedColumns[] = [];
    public multiSelectedSection: string;
    public dragList: any[] = [];
    public dragListCopy: any[] = [];
    public dragErase: any;
    public showReset = true;

    constructor(
        private changeDectRef: ChangeDetectorRef,
        private dialogReference: MdlDialogReference,
        @Inject(COLUMN_SETTING_TOKEN) private readonly columnSettingInfo: ColumnSettingInfo,
    ) {
        this.settingsInfo = columnSettingInfo;
        this.availableColumns = [...columnSettingInfo.availableColumns];
        this.selectedColumns = [...columnSettingInfo.selectedColumns];
        this.subheaderText = columnSettingInfo.subheaderText ? columnSettingInfo.subheaderText : this.subheaderText;
        this.header = columnSettingInfo.header ? columnSettingInfo.header : this.header;
        this.showReset = columnSettingInfo.showReset;
    }

    public drop(event: CdkDragDrop<string[]>) {
        if (event.item.element.nativeElement.classList.contains('selected')) {
            this.multiSelectDropListDropped(event);
            return;
        }
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            if (event.container.id === 'availableList') {
                let selectedItemId = event.item.element.nativeElement.id;
                let selectedItem = this.selectedColumns.find(t => t.key === selectedItemId);
                if (selectedItem && selectedItem.isMandatory) { return; }
            }
            transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
        }
    }

    public  multiSelectDragStarted(e: CdkDragStart) {
        if (e.source.element.nativeElement.classList.contains('selected')) { 
            let listData = e.source.dropContainer.data; 
            this.dragList = []; 
            this.dragListCopy = [...listData]; 
            let dragElement = e.source.element.nativeElement; 
            let container = Array.from(dragElement.parentElement!.children);
            let allSelected = document.querySelectorAll('.selected');

            allSelected.forEach((eli) => {
                let currentIndex = container.indexOf(eli);
                this.dragList.push(listData[currentIndex]);
                this.dragListCopy[currentIndex] = this.dragErase;
            });

        }
    }

    public multiSelectDropListDropped(event: CdkDragDrop<string[]>) {

        if (event.previousContainer === event.container) {
            let posAdjust = event.previousIndex < event.currentIndex ? 1 : 0; 
            this.dragListCopy.splice(event.currentIndex + posAdjust, 0, ...this.dragList);
            this.dragListCopy = this.dragListCopy.filter((el) => (el !== this.dragErase));
            for (let i = 0; i < event.container.data.length; i++) {
                event.container.data[i] = this.dragListCopy[i];
            }
        } else { 
            this.dragListCopy = this.dragListCopy.filter((el) => (el !== this.dragErase));

            this.dragListCopy.forEach((item, index) => {
                event.previousContainer.data[index] = this.dragListCopy[index];
            });

            this.dragList.forEach(item => {
                event.previousContainer.data.pop();
            });
            let otherListCopy = [...event.container.data];
            otherListCopy.splice(event.currentIndex, 0, ...this.dragList); 
            for (let i = 0; i < otherListCopy.length; i++) {
                event.container.data[i] = otherListCopy[i];
            }
        }

        let allSelected = document.querySelectorAll('.selected');
        allSelected.forEach((el) => {
            el.classList.remove('selected');
        });

        this.dragListCopy = [];
        this.dragList = [];
        this.multiSelectItems = [];
        this.multiSelectedSection = null;
    }

    public onResetClick() {
        let result = {
            availableColumns: this.availableColumns,
            selectedColumns: this.selectedColumns,
            resetSettings: true
        };
        this.dialogReference.hide(result);
    }

    public dragOnAvailableColumn(event: any) {
        this.dragStart(event);
        event.currentTarget.focus();
        const dragData = JSON.stringify({
            key: event.target.id,
            dragFrom: 'availableColumn'
        });
        event.dataTransfer.setData('dragData', dragData);
    }

    public dragOnSelectedColumn(event: any) {
        this.dragStart(event);
        event.currentTarget.focus();
        const dragData = JSON.stringify({
            key: event.target.id,
            dragFrom: 'selectedColumn'
        });
        event.dataTransfer.setData('dragData', dragData);
    }

    public allowDrop(event: any) {
        event.preventDefault();
    }

    public onColumnDragOver(event: any) {
        if (event.target.parentNode) {
            if (this.isBefore(this.selectedElement, event.target)) {
                if (this.isMandatoryField(event)) { return; }
                event.target.parentNode.insertBefore(this.selectedElement, event.target);

            } else {
                if (this.isMandatoryField(event)) { return; }
                event.target.parentNode.insertBefore(this.selectedElement, event.target.nextSibling);
            }
        }
    }

    public onAvailableColumnDrop(event: any) {
        event.preventDefault();
        !this.removeMultiSelectedColumns() && this.setList(event);
    }

    public onSelectedColumnDrop(event: MouseEvent) {
        event.preventDefault();
        !this.addMultiSelectedColumns() && this.setList(event);
    }

    public dragEnd() {
        this.selectedElement = null;
        this.selectedelementId = '';
    }

    public dragStart(event: any) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', null);
        this.selectedElement = event.target;
        this.selectedelementId = event.target.id;
    }

    public onAvailableColumnDoubleClick(event: any) {
        this.onSelectColumns('selectedColumns', event);
        event.target.classList.toggle('selected');
        this.multiSelectedSection = 'availableColumns';
    }

    public onSelectedColumnDoubleClick(event: any) {
        let column = this.columnSettingInfo.selectedColumns.find(sc => sc.key === event.target.id);
        if (column && column.isMandatory) { return; }
        this.onSelectColumns('availableColumns', event);
        event.target.classList.toggle('selected');
        this.multiSelectedSection = 'selectedColumns';
    }

    public onColumnClick(event: MouseEvent, listName: string) {
        if (listName === 'selected') {
            this.onSelectedColumnDoubleClick(event);
        }
        if (listName === 'available') {
            this.onAvailableColumnDoubleClick(event);
        }
    }

    public addColumns() {
        this.addMultiSelectedColumns();
        this.dragListCopy = [];
        this.dragList = [];
    }

    public removeColumns() {
        this.removeMultiSelectedColumns();
        this.dragListCopy = [];
        this.dragList = [];
    }

    public onApplyClick() {
        let result = {
            availableColumns: this.availableColumns,
            selectedColumns: this.selectedColumns,
            showReset: false
        };
        this.dialogReference.hide(result);
    }

    public onCancelClick() {
        this.selectedColumns = [...this.settingsInfo.selectedColumns];
        this.availableColumns = [...this.settingsInfo.availableColumns];
        this.dialogReference.hide();
    }

    private isBefore(el1: any, el2: any) {
        let cur;
        if (el2.parentNode === el1.parentNode) {
            for (cur = el1; cur; cur = cur.previousSibling) {
                if (cur === el2) { return true; }
            }
        }
        return false;
    }

    private setList(event: any) {
        if (this.selectedElement && this.selectedElement.id) {

            const list = Array.from(event.target.parentElement.childNodes)
                .filter((c: any) => c.id)
                .map((c: any) => { return ({ key: c.id, displayName: c.textContent.trim(), isMandatory: c.classList.contains('isMandatory')}); });
            const parentId = event.target.parentElement.id;

            if (list && list.length === 1) {
                if (list[0].key === 'placeholder') { // handle first item
                    if (event.target.parentElement.id === 'selectedColumns') {
                        this.selectedColumns = uniqBy([...this.selectedColumns, { key: this.selectedElement.id, displayName: this.selectedElement.textContent.trim() }], 'key');
                        this.availableColumns = this.availableColumns.filter(ac => ac.key !== this.selectedElement.id);
                    }
                    if (event.target.parentElement.id === 'availableColumns') {
                        this.availableColumns = uniqBy([...this.availableColumns, { key: this.selectedElement.id, displayName: this.selectedElement.textContent.trim() }], 'key');
                        this.selectedColumns = this.selectedColumns.filter(ac => ac.key !== this.selectedElement.id);
                    }
                }
                if (list[0].key === 'selectedColumns') { //dropping item on last of list
                    this.selectedColumns = uniqBy([...this.selectedColumns, { key: this.selectedElement.id, displayName: this.selectedElement.textContent.trim() }], 'key');
                    this.availableColumns = this.availableColumns.filter(ac => ac.key !== this.selectedElement.id);
                }
                if (list[0].key === 'availableColumns') { //dropping item on last of list
                    let column = this.columnSettingInfo.selectedColumns.find(sc => sc.key === this.selectedelementId);
                    if (column && !column.isMandatory) {
                        this.availableColumns = uniqBy([...this.availableColumns, { key: this.selectedElement.id, displayName: this.selectedElement.textContent.trim() }], 'key');
                        this.selectedColumns = this.selectedColumns.filter(ac => ac.key !== this.selectedElement.id);
                    }
                }
            } else {
                if (parentId === 'selectedColumns') { //dropping item on mid of list
                    this.selectedColumns = list;
                    this.availableColumns = this.availableColumns.filter(ac => ac.key !== this.selectedElement.id);
                }
                if (parentId === 'availableColumns') { //dropping item on mid of list
                    let column = this.columnSettingInfo.selectedColumns.find(sc => sc.key === this.selectedelementId);
                    if (column && !column.isMandatory) {
                        this.availableColumns = list;
                        this.selectedColumns = this.selectedColumns.filter(ac => ac.key !== this.selectedElement.id);
                    }
                }
            }
        }

        this.selectedElement = null;
        this.multiSelectItems = [];
        this.multiSelectedSection = null;
        this.changeDectRef.markForCheck();
    }

    private addMultiSelectedColumns() {
        if (this.multiSelectedSection === 'availableColumns' && this.multiSelectItems && this.multiSelectItems.length) {
            this.selectedColumns = uniqBy([...this.selectedColumns, ...this.multiSelectItems], 'key');
            this.availableColumns = this.availableColumns.filter(ac => !this.selectedColumns.some(sc => sc.key === ac.key));
            this.multiSelectItems = [];
            this.multiSelectedSection = null;
            return true;
        }
        return false;
    }

    private removeMultiSelectedColumns() {
        if (this.multiSelectedSection === 'selectedColumns' && this.multiSelectItems && this.multiSelectItems.length) {
            this.availableColumns = uniqBy([...this.availableColumns, ...this.multiSelectItems], 'key');
            this.selectedColumns = this.selectedColumns.filter(sc => !this.availableColumns.some(ac => ac.key === sc.key));
            this.multiSelectItems = [];
            this.multiSelectedSection = null;
            return true;
        }
        return false;
    }

    private onSelectColumns(selectedSection: string, event: any) {
        if (this.multiSelectedSection === selectedSection) {
            const selectedColumns = document.getElementById(selectedSection);
            const childNodes = Array.from(selectedColumns.childNodes).filter((c: any) => c.id);
            childNodes.forEach((ac: any) => ac.classList.remove('selected'));
            this.multiSelectItems = [];
            this.multiSelectedSection = null;
        }
        this.multiSelectItems = this.multiSelectItems.find(ms => ms.key === event.target.id) ?
            this.multiSelectItems.filter(ms => ms.key !== event.target.id) :
            [...this.multiSelectItems, { key: event.target.id, displayName: event.target.textContent.trim() }];
    }

    private isMandatoryField(event: any) {
        if (event.target.parentNode.id === 'availableColumns' && this.selectedelementId.length) {
            let column = this.columnSettingInfo.selectedColumns.find(sc => sc.key === this.selectedelementId);
            return column && column.isMandatory ? true : false;
        }
        return false;
    }
}